"use client";

import type { IHeader1046Widget } from "@/types/page";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "@/store/store";

/**
 * Header 1046 — BDU contact strip (bduedu.in `organisms/ContactBanner`).
 *
 * A 42px band pinned above the nav: right-aligned phone pill + WhatsApp pill on
 * a solid slate bar, hidden below 768px. Structurally Header 7 (dynamic pill
 * rows in `metadata` + a WhatsApp CTA), so it reuses the same columns; what
 * differs is the chrome — 16px/14px pill radii and the two inset highlight
 * shadows that give each pill its lit edge.
 *
 * Those shadows and the bar height are brand chrome baked into the reference,
 * so they stay hardcoded here rather than becoming CMS fields.
 *
 * v1 renders this `fixed` because that app hand-stacks its header layers
 * (the nav sits at `top-[42px]`). Here the strip is an ordinary section at the
 * top of the page, with `sticky_to_top` opting into pinning — same band, but it
 * participates in layout instead of being positioned around.
 *
 * BELOW 768px the reference drops the band entirely and shows the same two
 * contacts as round buttons pinned to the left edge (v1 renders them from the
 * root layout, next to `ContactBanner`). They carry the same data, so they ride
 * along here rather than becoming a second section: with `show_on_mobile` off
 * (the default) mobile gets the floating buttons, and turning it on swaps them
 * for the band itself.
 *
 * They share a left-edge column with the site-wide floating promo icon
 * (`FloatingIcons`/`OfferBox`), which is pinned to the same 44px centre line and
 * bottom anchor — so when the promo icon is live these stack above it in one
 * straight line instead of landing on top of it.
 */

/** BDU's pill chrome — inset highlights, not configurable in CMS. */
const PHONE_PILL_SHADOW =
  "inset 0 -0.5px 4px 0 rgba(255,255,255,0.25), inset 1px 0.6px 0 0 rgba(255,255,255,0.35)";
const WHATSAPP_PILL_SHADOW =
  "inset 0 -0.5px 1px 0 rgba(84,255,115,0.60), inset 1px 0.5px 1px 0 rgba(84,255,115,0.60)";

const DEFAULT_BAR_BG = "#354D6C";
const DEFAULT_PILL_BG = "rgba(255, 255, 255, 0.10)";
const DEFAULT_WHATSAPP_BG = "rgba(76, 175, 81, 0.70)";

/** Filled phone glyph (ContactBanner `PhoneIcon`), used when no icon is uploaded. */
const PhoneGlyph = ({ color, size = 18 }: { color: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M15.3122 16C13.6919 16 12.0909 15.6469 10.5095 14.9407C8.92798 14.2344 7.48909 13.2329 6.1928 11.9361C4.8965 10.6393 3.89524 9.20041 3.18902 7.61944C2.4828 6.03848 2.12942 4.43756 2.12891 2.81667C2.12891 2.58333 2.20668 2.38889 2.36224 2.23333C2.5178 2.07778 2.71224 2 2.94557 2H6.09557C6.27705 2 6.43909 2.0617 6.58168 2.18511C6.72428 2.30852 6.80854 2.45422 6.83446 2.62222L7.34002 5.34444C7.36594 5.55185 7.35946 5.72685 7.32057 5.86944C7.28168 6.01204 7.21039 6.13519 7.10668 6.23889L5.22057 8.14444C5.47983 8.62407 5.78757 9.08737 6.1438 9.53433C6.50002 9.9813 6.89228 10.4124 7.32057 10.8278C7.72242 11.2296 8.14372 11.6024 8.58446 11.9462C9.0252 12.29 9.49187 12.6042 9.98446 12.8889L11.8122 11.0611C11.9289 10.9444 12.0814 10.8571 12.2696 10.799C12.4578 10.7409 12.6424 10.7246 12.8234 10.75L15.5067 11.2944C15.6882 11.3463 15.8372 11.4404 15.9539 11.5768C16.0706 11.7131 16.1289 11.8653 16.1289 12.0333V15.1833C16.1289 15.4167 16.0511 15.6111 15.8956 15.7667C15.74 15.9222 15.5456 16 15.3122 16Z"
      fill={color}
    />
  </svg>
);

/** WhatsApp glyph (ContactBanner `WhatsAppIcon`), used when no icon is uploaded. */
const WhatsAppGlyph = ({ color, size = 18 }: { color: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M14.2871 3.68254C13.5994 2.98812 12.7804 2.43752 11.8777 2.06281C10.9751 1.68811 10.0069 1.4968 9.02961 1.50004C4.93461 1.50004 1.59711 4.83754 1.59711 8.93254C1.59711 10.245 1.94211 11.52 2.58711 12.645L1.53711 16.5L5.47461 15.465C6.56211 16.0575 7.78461 16.3725 9.02961 16.3725C13.1246 16.3725 16.4621 13.035 16.4621 8.94004C16.4621 6.95254 15.6896 5.08504 14.2871 3.68254ZM9.02961 15.1125C7.91961 15.1125 6.83211 14.8125 5.87961 14.25L5.65461 14.115L3.31461 14.73L3.93711 12.45L3.78711 12.2175C3.17027 11.2328 2.8428 10.0945 2.84211 8.93254C2.84211 5.52754 5.61711 2.75254 9.02211 2.75254C10.6721 2.75254 12.2246 3.39754 13.3871 4.56754C13.9628 5.14044 14.419 5.82195 14.7293 6.57254C15.0396 7.32313 15.1978 8.12785 15.1946 8.94004C15.2096 12.345 12.4346 15.1125 9.02961 15.1125ZM12.4196 10.4925C12.2321 10.4025 11.3171 9.95254 11.1521 9.88504C10.9796 9.82504 10.8596 9.79504 10.7321 9.97504C10.6046 10.1625 10.2521 10.5825 10.1471 10.7025C10.0421 10.83 9.92961 10.845 9.74211 10.7475C9.55461 10.6575 8.95461 10.455 8.24961 9.82504C7.69461 9.33004 7.32711 8.72254 7.21461 8.53504C7.10961 8.34754 7.19961 8.25004 7.29711 8.15254C7.37961 8.07004 7.48461 7.93504 7.57461 7.83004C7.66461 7.72504 7.70211 7.64254 7.76211 7.52254C7.82211 7.39504 7.79211 7.29004 7.74711 7.20004C7.70211 7.11004 7.32711 6.19504 7.17711 5.82004C7.02711 5.46004 6.86961 5.50504 6.75711 5.49754H6.39711C6.26961 5.49754 6.07461 5.54254 5.90211 5.73004C5.73711 5.91754 5.25711 6.36754 5.25711 7.28254C5.25711 8.19754 5.92461 9.08254 6.01461 9.20254C6.10461 9.33004 7.32711 11.205 9.18711 12.0075C9.62961 12.2025 9.97461 12.315 10.2446 12.3975C10.6871 12.54 11.0921 12.5175 11.4146 12.4725C11.7746 12.42 12.5171 12.0225 12.6671 11.5875C12.8246 11.1525 12.8246 10.785 12.7721 10.7025C12.7196 10.62 12.6071 10.5825 12.4196 10.4925Z"
      fill={color}
    />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Mobile floating contacts (v1 root layout, `md:hidden` button pair)   */
/* ------------------------------------------------------------------ */

/**
 * Geometry of the site-wide floating column (`FloatingIcons` + `OfferBox`), which
 * these buttons join below 768px:
 *   - the column is pinned `left-[10px] bottom-[50px]` with `gap-4`;
 *   - `OfferBox` adds `pl-1` around a 60px icon, so its centre line is 44px from
 *     the left edge and it is 72px tall (60px icon + `pb-3`).
 * Matching those keeps every icon on one straight line whether or not the promo
 * icon is switched on for the university.
 */
const FLOATING_LANE_WIDTH = 60;
const FLOATING_LANE_LEFT = 14;
const FLOATING_BOTTOM = 50;
const FLOATING_GAP = 16;
const OFFER_BOX_HEIGHT = 72;

/** Round phone button (v1 `PhoneSVG`) — mobile-only, brand chrome, not CMS. */
const PhoneCircleGlyph = () => (
  <svg
    width="50"
    height="50"
    viewBox="0 0 81 81"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g filter="url(#header1046-phone-shadow)">
      <rect x="8" y="4" width="65.75" height="65.75" rx="32.875" fill="#9F1D33" />
      <rect
        x="8.5"
        y="4.5"
        width="64.75"
        height="64.75"
        rx="32.375"
        stroke="#E8E8E8"
      />
      <path
        d="M55.7813 53.75C51.875 53.75 48.0156 52.8988 44.2031 51.1963C40.3906 49.4938 36.9219 47.0794 33.7969 43.9531C30.6719 40.8269 28.2581 37.3581 26.5556 33.5469C24.8531 29.7356 24.0012 25.8763 24 21.9688C24 21.4063 24.1875 20.9375 24.5625 20.5625C24.9375 20.1875 25.4063 20 25.9688 20H33.5625C34 20 34.3906 20.1487 34.7344 20.4462C35.0781 20.7437 35.2813 21.095 35.3438 21.5L36.5625 28.0625C36.625 28.5625 36.6094 28.9844 36.5156 29.3281C36.4219 29.6719 36.25 29.9687 36 30.2187L31.4531 34.8125C32.0781 35.9687 32.82 37.0856 33.6787 38.1631C34.5375 39.2406 35.4831 40.28 36.5156 41.2813C37.4844 42.25 38.5 43.1488 39.5625 43.9775C40.625 44.8063 41.75 45.5638 42.9375 46.25L47.3437 41.8437C47.625 41.5625 47.9925 41.3519 48.4463 41.2119C48.9 41.0719 49.345 41.0325 49.7812 41.0938L56.25 42.4062C56.6875 42.5312 57.0469 42.7581 57.3281 43.0869C57.6094 43.4156 57.75 43.7825 57.75 44.1875V51.7813C57.75 52.3438 57.5625 52.8125 57.1875 53.1875C56.8125 53.5625 56.3438 53.75 55.7813 53.75Z"
        fill="white"
      />
    </g>
    <defs>
      <filter
        id="header1046-phone-shadow"
        x="0.8"
        y="0.8"
        width="80.15"
        height="80.15"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="3.6" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="header1046-phone-shadow-blend"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="header1046-phone-shadow-blend"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

/** Round WhatsApp button (v1 `WhatsAppSVG2`) — mobile-only brand chrome. */
const WhatsAppCircleGlyph = () => (
  <svg
    width="51"
    height="50"
    viewBox="0 0 31 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g clipPath="url(#header1046-whatsapp-clip)">
      <path
        d="M1.61912 15.3265C1.61844 17.7631 2.26006 20.1422 3.4801 22.2392L1.50244 29.404L8.89199 27.4815C10.9359 28.5855 13.2258 29.164 15.5529 29.1641H15.559C23.2412 29.1641 29.4946 22.9614 29.4979 15.3374C29.4994 11.6431 28.0508 8.16914 25.4188 5.55554C22.7873 2.94217 19.2875 1.50217 15.5585 1.50049C7.87541 1.50049 1.62241 7.70289 1.61923 15.3265"
        fill="url(#header1046-whatsapp-fill)"
      />
      <path
        d="M1.12121 15.322C1.12042 17.8462 1.78504 20.3104 3.04857 22.4825L1 29.9041L8.65451 27.9127C10.7636 29.0537 13.1382 29.6553 15.5545 29.6562H15.5607C23.5185 29.6562 29.9965 23.2303 29.9999 15.3334C30.0013 11.5063 28.5006 7.90751 25.7746 5.20028C23.0482 2.49339 19.4232 1.00157 15.5607 1C7.60156 1 1.12438 7.42496 1.12121 15.322ZM5.67975 22.1084L5.39394 21.6582C4.19249 19.7627 3.55834 17.5722 3.55925 15.3229C3.56174 8.75907 8.94531 3.41891 15.5652 3.41891C18.7711 3.42026 21.7839 4.66029 24.05 6.91016C26.3159 9.16024 27.5628 12.1513 27.562 15.3325C27.5591 21.8963 22.1754 27.2371 15.5607 27.2371H15.5559C13.4021 27.236 11.2898 26.6621 9.44759 25.5775L9.0092 25.3195L4.46685 26.5012L5.67975 22.1084Z"
        fill="url(#header1046-whatsapp-ring)"
      />
      <path
        d="M11.9517 9.3344C11.6814 8.73833 11.397 8.7263 11.1399 8.71585C10.9295 8.70686 10.6889 8.70753 10.4485 8.70753C10.2079 8.70753 9.81693 8.79734 9.48649 9.15535C9.15571 9.51369 8.22363 10.3796 8.22363 12.1409C8.22363 13.9021 9.51651 15.6044 9.69674 15.8434C9.8772 16.0821 12.1927 19.8121 15.8598 21.247C18.9075 22.4395 19.5277 22.2023 20.1892 22.1425C20.8507 22.0829 22.3238 21.2768 22.6244 20.4408C22.9251 19.605 22.9251 18.8886 22.835 18.7388C22.7448 18.5897 22.5042 18.5001 22.1434 18.3211C21.7826 18.1422 20.0087 17.276 19.678 17.1565C19.3473 17.0372 19.1068 16.9776 18.8662 17.336C18.6256 17.6939 17.9346 18.5001 17.7241 18.7388C17.5137 18.9781 17.3031 19.0079 16.9424 18.8289C16.5814 18.6492 15.4195 18.2717 14.041 17.0522C12.9684 16.1033 12.2443 14.9315 12.0338 14.5731C11.8234 14.2152 12.0113 14.0212 12.1922 13.8428C12.3543 13.6824 12.5531 13.4248 12.7337 13.2158C12.9136 13.0067 12.9736 12.8576 13.0939 12.6188C13.2143 12.3799 13.1541 12.1708 13.064 11.9917C12.9736 11.8127 12.2725 10.0422 11.9517 9.3344Z"
        fill="white"
      />
    </g>
    <defs>
      <linearGradient
        id="header1046-whatsapp-fill"
        x1="1401.28"
        y1="2791.85"
        x2="1401.28"
        y2="1.50049"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#1FAF38" />
        <stop offset="1" stopColor="#60D669" />
      </linearGradient>
      <linearGradient
        id="header1046-whatsapp-ring"
        x1="1451"
        y1="2891.41"
        x2="1451"
        y2="1"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F9F9F9" />
        <stop offset="1" stopColor="white" />
      </linearGradient>
      <clipPath id="header1046-whatsapp-clip">
        <rect width="29" height="29" fill="white" transform="translate(1 1)" />
      </clipPath>
    </defs>
  </svg>
);

type ContactPill = {
  label: string;
  value: string;
  link?: string;
};

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

/** Same shapes Header 7 has to absorb: array, JSON string, or `{ "0": row }`. */
function normalizeMetadataInput(raw: unknown): IHeader1046Widget[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw as IHeader1046Widget[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as IHeader1046Widget[]) : undefined;
    } catch {
      return undefined;
    }
  }
  if (typeof raw === "object") {
    const keys = Object.keys(raw as Record<string, unknown>);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
      return Object.values(raw as Record<string, unknown>) as IHeader1046Widget[];
    }
  }
  return undefined;
}

function pillsFromMetadata(
  metadata: IHeader1046Widget[] | undefined,
): ContactPill[] {
  if (!metadata?.length) return [];

  return [...metadata]
    .filter((m) => m.status !== false)
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((m) => ({
      label: str(m.widget_label),
      value: str(m.widget_value),
      link: str(m.widget_link) || undefined,
    }))
    .filter((m) => m.label || m.value);
}

/** v1's mobile phone button dials the first pill; fall back to its raw number. */
function buildTelLink(pill: ContactPill | undefined): string {
  if (!pill) return "";
  const link = str(pill.link);
  if (link) return link;
  const digits = str(pill.value).replace(/\D/g, "");
  return digits ? `tel:+${digits}` : "";
}

/** Build a WhatsApp deep link from a raw number when no explicit link is set. */
function buildWhatsAppLink(link: string, number: string): string {
  if (link) return link;
  const digits = number.replace(/\D/g, "");
  return digits ? `https://api.whatsapp.com/send?phone=+${digits}` : "";
}

function shouldUnoptimizeImageSrc(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

const Header1046 = ({ data }: { data: any }) => {
  const sectionData = data?.data ?? data;
  const isPreview = Boolean(data?.isPreview ?? false);

  const stickyToTop = toBool(sectionData?.sticky_to_top, false);
  const rootRef = useRef<HTMLDivElement>(null);

  /* Publish the pinned band's height so a sticky navbar can offset by it —
     same custom property Header 4 / Header 7 write. */
  useLayoutEffect(() => {
    if (!stickyToTop) {
      document.documentElement.style.removeProperty("--header4-sticky-height");
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--header4-sticky-height",
        `${el.offsetHeight}px`,
      );
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--header4-sticky-height");
    };
  }, [stickyToTop]);

  /* The floating promo icon (`FloatingIcons` → `OfferBox`) sits at the bottom of
     the shared left column, so the mobile contact buttons stack above it when it
     is live. Its state is written by a client effect, so it is only read after
     mount — using it on the server render would hydrate to a different offset. */
  const promoBanner = useSelector(
    (state: RootState) => state.globalslice.promotional_banner,
  );
  const promoIconEnabled = useSelector(
    (state: RootState) =>
      state.globalslice.floatingIconsVisibility.isPromotionalBannerEnabled,
  );
  const promoModalOpen = useSelector(
    (state: RootState) => state.globalslice.isPromotionalBannerOpen,
  );
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!sectionData) return null;

  const {
    background_color,
    pill_background_color,
    label_text_color = "#FFFFFF",
    phone_text_color = "#FFFFFF",
    phone_icon,
    phone_icon_alt = "Phone",
    metadata,
    whatsapp_number = "",
    whatsapp_link = "",
    whatsapp_button_text = "WhatsApp Chat",
    whatsapp_button_color,
    whatsapp_button_text_color = "#FFFFFF",
    whatsapp_icon,
  } = sectionData;

  const barBg = str(background_color) || DEFAULT_BAR_BG;
  const pillBg = str(pill_background_color) || DEFAULT_PILL_BG;
  const waBg = str(whatsapp_button_color) || DEFAULT_WHATSAPP_BG;
  const phoneIconSrc = str(phone_icon);
  const waIconSrc = str(whatsapp_icon);

  const pills = pillsFromMetadata(normalizeMetadataInput(metadata));
  const waLink = buildWhatsAppLink(str(whatsapp_link), str(whatsapp_number));
  const showWhatsApp = toBool(sectionData?.whatsapp_show, true) && !!waLink;
  const waText = str(whatsapp_button_text) || "WhatsApp Chat";

  // v1 hides the strip under 768px; the toggle opts back in.
  const showOnMobile = toBool(sectionData?.show_on_mobile, false);

  /* Mobile keeps the same two contacts, as the reference's left-edge buttons —
     unless the strip itself was opted into mobile, which then stands in for them. */
  const telLink = buildTelLink(pills[0]);
  const showFloatingContacts =
    !isPreview && !showOnMobile && Boolean(telLink || showWhatsApp);
  const offerBoxVisible =
    mounted &&
    Boolean(promoIconEnabled) &&
    !promoModalOpen &&
    Boolean(promoBanner?.enabled) &&
    Boolean(promoBanner?.mobile_image || promoBanner?.desktop_image);
  const floatingBottom =
    FLOATING_BOTTOM + (offerBoxVisible ? OFFER_BOX_HEIGHT + FLOATING_GAP : 0);
  // The label ("Phone NO.") is strip chrome, so the button announces the number.
  const telLabel = pills[0]?.value ? `Call ${pills[0].value}` : "Call us";

  if (!pills.length && !showWhatsApp) return null;

  return (
    <>
      <div
        ref={rootRef}
        className={cn(
          "h-[42px] w-full items-center justify-end px-[38px] py-2 min-[1024px]:pl-[45px] min-[1024px]:pr-[38px]",
          showOnMobile ? "flex" : "hidden tab:flex",
          stickyToTop && !isPreview && "sticky top-0 z-[100]",
        )}
        style={{ backgroundColor: barBg }}
      >
        <div className="flex items-center gap-4">
          {pills.map((pill, index) => {
            const content = (
              <>
                {phoneIconSrc ? (
                  <Image
                    src={phoneIconSrc}
                    alt={str(phone_icon_alt) || "Phone"}
                    width={18}
                    height={18}
                    className="size-[18px] shrink-0 object-contain"
                    unoptimized={shouldUnoptimizeImageSrc(phoneIconSrc)}
                  />
                ) : (
                  <PhoneGlyph color={label_text_color} size={18} />
                )}
                <span
                  className="font-sans text-xs font-semibold leading-normal"
                  style={{ color: phone_text_color }}
                >
                  {[pill.label, pill.value].filter(Boolean).join(" ")}
                </span>
              </>
            );

            const pillClass =
              "flex items-center gap-2 rounded-[16px] px-2.5 py-1";
            const pillStyle = {
              backgroundColor: pillBg,
              boxShadow: PHONE_PILL_SHADOW,
            };

            return pill.link ? (
              <a
                key={`${pill.label}-${index}`}
                href={pill.link}
                className={pillClass}
                style={pillStyle}
              >
                {content}
              </a>
            ) : (
              <div
                key={`${pill.label}-${index}`}
                className={pillClass}
                style={pillStyle}
              >
                {content}
              </div>
            );
          })}

          {showWhatsApp ? (
            <Link
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-[14px] px-2.5 py-1"
              style={{ backgroundColor: waBg, boxShadow: WHATSAPP_PILL_SHADOW }}
            >
              {waIconSrc ? (
                <Image
                  src={waIconSrc}
                  alt={waText}
                  width={18}
                  height={18}
                  className="size-[18px] shrink-0 object-contain"
                  unoptimized={shouldUnoptimizeImageSrc(waIconSrc)}
                />
              ) : (
                <WhatsAppGlyph color={whatsapp_button_text_color} size={18} />
              )}
              <span
                className="font-sans text-xs font-medium leading-normal"
                style={{ color: whatsapp_button_text_color }}
              >
                {waText}
              </span>
            </Link>
          ) : null}
        </div>
      </div>

      {showFloatingContacts ? (
        <div
          className="fixed z-40 flex flex-col items-start gap-4 tab:hidden"
          style={{ left: FLOATING_LANE_LEFT, bottom: floatingBottom }}
        >
          {telLink ? (
            <a
              href={telLink}
              aria-label={telLabel}
              className="flex justify-center"
              style={{ width: FLOATING_LANE_WIDTH }}
            >
              <PhoneCircleGlyph />
            </a>
          ) : null}

          {showWhatsApp ? (
            <Link
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={waText}
              className="flex justify-center"
              style={{ width: FLOATING_LANE_WIDTH }}
            >
              <WhatsAppCircleGlyph />
            </Link>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default Header1046;
